import { z } from 'zod';

export enum Agent {
  MONETIZE_NOW_ASSISTANT = 'mn-assistant',
  METABASE_AGENT = 'metabase-agent',
}

/**
 * One thing the agent did while working on a reply: a tool call, or a line of
 * narration a sub-agent wrote before calling one ("Let me check the contract
 * fields first.").
 */
export const ActivityStepSchema = z.object({
  /** The tool call id, which links a call's `tool_start` to its `tool_end`. */
  id: z.string(),
  kind: z.enum(['tool', 'note']),
  toolName: z.string().optional(),
  /** Narration text for a note; the tool's arguments (JSON) for a tool. */
  input: z.string().optional(),
  output: z.string().optional(),
  /** How many records a search or query returned, read from the full output
   * before it is capped. */
  resultCount: z.number().optional(),
  status: z.enum(['running', 'done', 'failed', 'stopped']),
  startedAt: z.number(),
  endedAt: z.number().optional(),
});

export type ActivityStep = z.infer<typeof ActivityStepSchema>;

/** An unbroken stretch of work, shown as one collapsible group in the turn. */
export const ActivitySegmentSchema = z.object({
  /** How many of the turn's visible replies came before it, which places it
   * among them (0 = above the first reply). */
  position: z.number().int(),
  startedAt: z.number(),
  /** Set once a reply starts streaming after this work, or the turn ends. */
  endedAt: z.number().optional(),
  steps: z.array(ActivityStepSchema),
});

export type ActivitySegment = z.infer<typeof ActivitySegmentSchema>;

/**
 * Everything the agent did to answer one prompt, with timings. Built from
 * the SSE stream as it arrives and persisted with the conversation; the
 * history endpoint has no timings, so a turn restored from the server only
 * has this if it was carried over from local storage.
 */
export const TurnActivitySchema = z.object({
  startedAt: z.number(),
  endedAt: z.number().optional(),
  status: z.enum(['running', 'done', 'stopped']),
  /** What the agent is doing between tool calls. Only meaningful while
   * running; a running tool step takes precedence over it. */
  phase: z.enum(['thinking', 'drafting', 'finalizing']),
  segments: z.array(ActivitySegmentSchema),
});

export type TurnActivity = z.infer<typeof TurnActivitySchema>;

export const MessageSchema = z.object({
  id: z.string(),
  content: z.string(),
  role: z.enum(['user', 'assistant']),
  type: z.enum(['standard', 'error', 'final_result']),
  timestamp: z.date(),
  error: z
    .object({
      type: z.enum(['error', 'tool_error', 'llm_error', 'chain_error']),
      message: z.string(),
    })
    .optional(),
  isSubAgentMessage: z.boolean().optional(),
  /** Only on sub-agent messages: set once the model step that wrote it ended
   * without calling a tool, which makes it a write-up of the findings rather
   * than narration ahead of a call. */
  isWriteUp: z.boolean().optional(),
  /** Marks the synthetic greeting seeded on session init/reset -- never
   * present in a server-reconstructed history, so excluded from any
   * comparison against it. */
  isHelloMessage: z.boolean().optional(),
  /** Original user prompt stored on error messages to enable retry */
  prompt: z.string().optional(),
  /** Only on user messages: the work the agent did answering this prompt.
   * Kept on the prompt rather than the reply because a turn can end with
   * no reply, or several, and prompts line up one-to-one with the server's
   * history when a session is restored. */
  activity: TurnActivitySchema.optional(),
});

export type Message = z.infer<typeof MessageSchema>;

export const ChatSocketContextTypeSchema = z.object({
  agent: z.enum(Agent),
  setAgent: z.function({ input: [z.enum(Agent)], output: z.void() }),
  messages: z.array(MessageSchema),
  isLoading: z.boolean(),
  isThinking: z.boolean(),
  /** True while reconciling with the backend after a dropped connection
   * (live mid-send) or while polling a still-running turn found on mount. */
  isReconnecting: z.boolean(),
  /** True only during the brief mount-time window between finding a saved
   * session and deciding what to render for it (server unreachable, already
   * completed, or still running) -- nothing conversation-related should
   * render while this is true, to avoid a jump once the decision is made. */
  isRestoringSession: z.boolean(),
  sessionId: z.string().nullable(),
  sendMessage: z.function({ input: [z.string()], output: z.void() }),
  retryMessage: z.function({ input: [z.string()], output: z.void() }),
  reset: z.function({ input: [], output: z.void() }),
  disconnect: z.function({ input: [], output: z.void() }),
});

export type ChatSocketContextType = z.infer<typeof ChatSocketContextTypeSchema>;

export const UsageMetadataSchema = z.object({
  input_tokens: z.int(),
  output_tokens: z.int(),
});

export type UsageMetadata = z.infer<typeof UsageMetadataSchema>;

export const EventTypes = z.enum([
  'graph_start',
  'model_start',
  'model_end',
  'token',
  'message',
  'tool_start',
  'tool_end',
  'error',
  'tool_error',
  'llm_error',
  'chain_error',
  'heartbeat',
  'end',
  'usage_limit_exceeded',
]);

export type EventTypes = z.infer<typeof EventTypes>;

const EventBaseSchema = z.object({
  type: EventTypes,
  session_id: z.string(),
  step: z.int().nullable().optional(),
  timestamp: z.string().default(() => new Date().toISOString()),
  tags: z.array(z.string()).optional(),
});

export const GraphStartEventSchema = EventBaseSchema.extend({
  type: z.literal('graph_start'),
  query: z.string(),
});

export type GraphStartEvent = z.infer<typeof GraphStartEventSchema>;

export const ModelStartEventSchema = EventBaseSchema.extend({
  type: z.literal('model_start'),
  message_count: z.int().nullish(),
});

export type ModelStartEvent = z.infer<typeof ModelStartEventSchema>;

export const ModelEndEventSchema = EventBaseSchema.extend({
  type: z.literal('model_end'),
  response_metadata: z.record(z.string(), z.unknown()),
});

export type ModelEndEvent = z.infer<typeof ModelEndEventSchema>;

export const TokenEventSchema = EventBaseSchema.extend({
  type: z.literal('token'),
  token: z.any(),
});

export type TokenEvent = z.infer<typeof TokenEventSchema>;

/**
 * A complete message, e.g. a sub-agent's final response when it ends the run
 * without handing back to the supervisor (tagged "response").
 */
export const ChatMessageEventSchema = EventBaseSchema.extend({
  type: z.literal('message'),
  message_id: z.string().nullish(),
  content: z.string(),
});

export type ChatMessageEvent = z.infer<typeof ChatMessageEventSchema>;

export const ToolStartEventSchema = EventBaseSchema.extend({
  type: z.literal('tool_start'),
  tool_name: z.string(),
  /** Matches the `id` of the `tool_use` token block that announced the call,
   * and the `tool_call_id` of its `tool_end`. */
  tool_call_id: z.string().nullish(),
  input: z.string(),
});

export type ToolStartEvent = z.infer<typeof ToolStartEventSchema>;

export const ToolEndEventSchema = EventBaseSchema.extend({
  type: z.literal('tool_end'),
  /**
   * Present on the backend's `tool_end` frames; kept optional so an older
   * backend that omits it can never fail parsing (an unparseable frame makes
   * the stream look dropped and triggers a full reconnect cycle).
   */
  tool_name: z.string().nullish(),
  tool_call_id: z.string().nullish(),
  output: z.string().nullish(),
  /** The tool's result: a string, or a list of `{ type: 'text', text }`
   * blocks. Untyped beyond that so an unexpected shape can't fail parsing. */
  content: z.unknown().optional(),
});

export type ToolEndEvent = z.infer<typeof ToolEndEventSchema>;

export const ErrorEventSchema = EventBaseSchema.extend({
  type: z.literal('error'),
  error: z.string(),
});

export type ErrorEvent = z.infer<typeof ErrorEventSchema>;

export const ToolErrorEventSchema = EventBaseSchema.extend({
  type: z.literal('tool_error'),
  /** The failed call's id; a failed call gets no `tool_end`. */
  tool_call_id: z.string().nullish(),
  error: z.string(),
});

export type ToolErrorEvent = z.infer<typeof ToolErrorEventSchema>;

export const LLMErrorEventSchema = EventBaseSchema.extend({
  type: z.literal('llm_error'),
  error: z.string(),
});

export type LLMErrorEvent = z.infer<typeof LLMErrorEventSchema>;

export const ChainErrorEventSchema = EventBaseSchema.extend({
  type: z.literal('chain_error'),
  error: z.string(),
});

export type ChainErrorEvent = z.infer<typeof ChainErrorEventSchema>;

export const HeartbeatEventSchema = EventBaseSchema.extend({
  type: z.literal('heartbeat'),
});

export type HeartbeatEvent = z.infer<typeof HeartbeatEventSchema>;

export const EndEventSchema = EventBaseSchema.extend({
  type: z.literal('end'),
  /**
   * Usage telemetry is advisory and not consumed by the UI. Kept optional so a
   * missing/null value can never fail parsing — an unparseable `end` frame
   * leaves the stream looking dropped and triggers a full reconnect cycle.
   */
  usage: z.record(z.string(), UsageMetadataSchema).nullish(),
  session_usage: z.record(z.string(), UsageMetadataSchema).nullish(),
});

export type EndEvent = z.infer<typeof EndEventSchema>;

export const UsageLimitExceededEventSchema = EventBaseSchema.extend({
  type: z.literal('usage_limit_exceeded'),
  message: z.string(),
  input_tokens: z.number(),
  output_tokens: z.number(),
});

export type UsageLimitExceededEvent = z.infer<
  typeof UsageLimitExceededEventSchema
>;

export const EventSchema = z.discriminatedUnion('type', [
  GraphStartEventSchema,
  ModelStartEventSchema,
  ModelEndEventSchema,
  TokenEventSchema,
  ChatMessageEventSchema,
  ToolStartEventSchema,
  ToolEndEventSchema,
  ErrorEventSchema,
  ToolErrorEventSchema,
  LLMErrorEventSchema,
  ChainErrorEventSchema,
  HeartbeatEventSchema,
  EndEventSchema,
  UsageLimitExceededEventSchema,
]);

export type Event = z.infer<typeof EventSchema>;

/**
 * Shape of a message as returned by GET /sessions/{id}/history -- the raw
 * internal LangChain/LangGraph message list, not the live SSE event shape
 * above. Only the fields this endpoint's frontend consumer actually uses
 * are typed here (`type`, `content`, `id`, `name`); the backend response
 * carries more (tool_calls, tool_call_id, additional_kwargs,
 * response_metadata) that aren't needed for reconstructing the transcript.
 */
export const ChatHistoryMessageSchema = z.object({
  type: z.string(),
  content: z.unknown(),
  id: z.string().nullish(),
  name: z.string().nullish(),
  tool_calls: z.array(z.unknown()).nullish(),
});

export type ChatHistoryMessage = z.infer<typeof ChatHistoryMessageSchema>;

export const SessionHistoryResponseSchema = z.object({
  session_id: z.string(),
  turn_status: z.enum(['running', 'completed']),
  messages: z.array(ChatHistoryMessageSchema),
});

export type SessionHistoryResponse = z.infer<
  typeof SessionHistoryResponseSchema
>;
